import {
    Theme,
    ThemeColor,
    ThemeColors,
    ThemeColorVariants,
    varPrefix,
} from './theme';
import { colors } from './colors';

export const PALETTE_PREFIX = 'palette';
export const COLOR_PREFIX = 'color';

const ColorOptions: ThemeColorVariants = {
    default: '500',
    hover: '600',
    active: '700',
    map: '700',
    'map-active': '800',
    light: '100',
    'light-hover': '200',
    'light-active': '300',
    border: '300',
};

const ColorOptionsDark: ThemeColorVariants = {
    default: '600',
    hover: '500',
    active: '400',
    map: '400',
    'map-active': '300',
    light: '800',
    'light-hover': '700',
    'light-active': '600',
    border: '600',
};

function buildPalette() {
    const ret: string[] = [];
    const colorKeys = Object.keys(colors);
    for (const key of colorKeys) {
        const color = colors[key as keyof typeof colors];
        if (typeof color === 'string') {
            ret.push(`${varPrefix()}-${PALETTE_PREFIX}-${key}: ${color}`);
        } else {
            const keyWeights = Object.keys(color);
            for (const weight of keyWeights) {
                // @ts-ignore
                const weightColor = color[weight];
                ret.push(
                    `${varPrefix()}-${PALETTE_PREFIX}-${key}-${weight}: ${weightColor}`
                );
            }
        }
    }
    return ret;
}

function buildColors(color: ThemeColors, colorOptions: ThemeColorVariants) {
    function buildOneColor(name: string, value: ThemeColor) {
        const ret: string[] = [];
        if (typeof value === 'string') {
            for (const variant of Object.keys(colorOptions)) {
                ret.push(
                    `${varPrefix()}-${COLOR_PREFIX}-${name}-${variant}: ${
                        // @ts-ignore
                        colors[value][colorOptions[variant]]
                    }`
                );
            }
        } else {
            for (const variant of Object.keys(value)) {
                ret.push(
                    `${varPrefix()}-${COLOR_PREFIX}-${name}-${variant}: ${
                        value[variant as keyof ThemeColorVariants]
                    }`
                );
            }
        }
        ret.push(
            `${varPrefix()}-${COLOR_PREFIX}-${name}: var(${varPrefix()}-${COLOR_PREFIX}-${name}-default)`
        );
        return ret;
    }

    return [
        ...buildOneColor('primary', color.primary),
        ...buildOneColor('success', color.success),
        ...buildOneColor('warning', color.warning),
        ...buildOneColor('neutral', color.neutral),
    ];
}

function buildMap(category: string, data: any) {
    return Object.keys(data).map((key) => {
        return `${varPrefix()}-${category}-${key}: ${data[key]}`;
    });
}

export const buildCSSVars = (
    theme: Theme,
    colorOverrides: Partial<ThemeColors>
): string => {
    const vars: string[] = [];
    vars.push(...buildPalette());
    vars.push(
        ...buildColors({ ...theme.color, ...colorOverrides }, ColorOptions)
    );

    vars.push(...buildMap('background', theme.background.light));
    vars.push(...buildMap('text', theme.text.light));
    vars.push(...buildMap('icon', theme.icon));
    vars.push(...buildMap('spacing', theme.spacing));
    vars.push(...buildMap('shadow', theme.shadow));
    vars.push(...buildMap('radius', theme.radius));
    vars.push(...buildMap('breakpoints', theme.breakpoints));
    vars.push(...buildMap('jobCardMediaHeight', theme.jobCardMedia.height));
    vars.push(...buildMap('jobCardMediaWidth', theme.jobCardMedia.width));

    return vars.join(';\n');
};

export const buildDarkCSSVars = (
    theme: Theme,
    colorOverrides: Partial<ThemeColors>
): string => {
    const vars: string[] = [];
    vars.push(
        ...buildColors({ ...theme.color, ...colorOverrides }, ColorOptionsDark)
    );
    vars.push(...buildMap('background', theme.background.dark));
    vars.push(...buildMap('text', theme.text.dark));

    return vars.join(';\n');
};
